// src/components/ShootingBlockViewer.tsx
import { useEffect, useMemo, useState, useCallback } from "react";
import { supabase } from "../supabaseClient";
import { Target } from "lucide-react";
import i18n from "../i18n";

/* ---- mini-hook i18n safe (même que sur tes autres pages) ---- */
function useI18nSafe() {
  const [lng, setLng] = useState(
    (i18n.resolvedLanguage || i18n.language || "fr").toLowerCase()
  );
  useEffect(() => {
    const onChange = (l: string) => setLng(l.toLowerCase());
    i18n.on("languageChanged", onChange);
    return () => i18n.off("languageChanged", onChange);
  }, []);
  const t = useCallback(
    (key: string, options?: Record<string, unknown>): string =>
      options != null
        ? String((i18n.t as any)(key, { ...options, lng }))
        : String((i18n.t as any)(key, { lng })),
    [lng]
  );
  return { t, lng };
}

export default function ShootingBlockViewer({ trainingId }: { trainingId: string }) {
  const { t } = useI18nSafe();

  const [block, setBlock] = useState<any>(null);
  const [rows, setRows] = useState<any[]>([]);

  useEffect(() => {
    (async () => {
      const { data: blocks } = await supabase
        .from("training_shoot_blocks")
        .select("*")
        .eq("training_id", trainingId)
        .order("created_at", { ascending: true })
        .limit(1);

      const b = blocks?.[0];
      if (!b) return;
      setBlock(b);

      const { data: entries } = await supabase
        .from("training_shoot_entries")
        .select("*")
        .eq("block_id", b.id)
        .order("created_at", { ascending: true });

      setRows(entries ?? []);
    })();
  }, [trainingId]);

  const totals = useMemo(() => {
    const m = new Map<number, { name: string; made: number; attempted: number }>();
    rows.forEach(r => {
      const cur = m.get(r.player_number) ?? { name: r.player_name, made: 0, attempted: 0 };
      cur.made += r.made;
      cur.attempted += r.attempted;
      m.set(r.player_number, cur);
    });
    return [...m.entries()].sort((a, b) => a[0] - b[0]);
  }, [rows]);

  if (!block) return null;

  // titre: si vide, on montre le libellé i18n (pas une valeur “réelle”)
  const title = (block.title && String(block.title).trim())
    ? block.title
    : t("shooting:defaultTitle");

  return (
    <section className="mt-6">
      <div className="flex items-center gap-2 mb-2">
        <Target className="w-5 h-5" />
        <h3 className="text-lg font-semibold">{title}</h3>
      </div>

      {block.notes && (
        <p className="mb-3 text-sm text-gray-600 dark:text-gray-300 whitespace-pre-line">
          {block.notes}
        </p>
      )}

      {/* Tableau récap */}
      <div className="overflow-x-auto">
        <table className="min-w-[520px] text-sm">
          <thead>
            <tr className="text-left border-b border-gray-200 dark:border-gray-800">
              <th className="py-2 pr-2">{t("shooting:columns.player")}</th>
              <th className="py-2 px-2">{t("shooting:columns.total")}</th>
              <th className="py-2 px-2">{t("shooting:columns.pct")}</th>
            </tr>
          </thead>
          <tbody>
            {totals.map(([num, agg]) => {
              const pct = agg.attempted > 0 ? Math.round((agg.made / agg.attempted) * 100) : 0;
              return (
                <tr key={num} className="border-b border-gray-100 dark:border-gray-800">
                  <td className="py-2 pr-2">
                    {agg.name} <span className="text-xs text-gray-500">#{num}</span>
                  </td>
                  <td className="py-2 px-2">{agg.made} / {agg.attempted}</td>
                  <td className="py-2 px-2">{pct}%</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Détail */}
      <div className="mt-4 overflow-x-auto">
        <table className="min-w-[720px] text-xs">
          <thead>
            <tr className="text-left border-b border-gray-200 dark:border-gray-800">
              <th className="py-2 pr-2">{t("shooting:columns.player")}</th>
              <th className="py-2 px-2">{t("shooting:columns.zone")}</th>
              <th className="py-2 px-2">{t("shooting:columns.made")}</th>
              <th className="py-2 px-2">{t("shooting:columns.attempted")}</th>
              <th className="py-2 px-2">{t("shooting:columns.pct")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const pct = r.attempted > 0 ? Math.round((r.made / r.attempted) * 100) : 0;
              return (
                <tr key={r.id} className="border-b border-gray-100 dark:border-gray-800">
                  <td className="py-2 pr-2">
                    {r.player_name} <span className="text-[10px] text-gray-500">#{r.player_number}</span>
                  </td>
                  <td className="py-2 px-2">{r.location}</td>
                  <td className="py-2 px-2">{r.made}</td>
                  <td className="py-2 px-2">{r.attempted}</td>
                  <td className="py-2 px-2">{pct}%</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}
